import React, { useState, useEffect } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Button from "@mui/material/Button";
import { articleService } from "../services/articleService";
import Link from "@mui/material/Link";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PinterestIcon from "@mui/icons-material/Pinterest";
import EmailIcon from "@mui/icons-material/Email";
import { Helmet } from "react-helmet-async";

export type blockType = {
  type: string;
  text?: string;
  src?: string;
  alt?: string;
  items?: string[];
};

const ArticleDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const articlesData = await articleService.getArticles();
        const currentLang = (i18n.language || "en") as "en" | "ru";
        const hasLangContent = (langContent: any): boolean => {
          if (!langContent) return false;
          const hasTitle = Boolean(
            langContent.title && String(langContent.title).trim()
          );
          const hasContentTitle = Boolean(
            langContent.contentTitle && String(langContent.contentTitle).trim()
          );
          const content = langContent.content;
          const hasContent = Array.isArray(content)
            ? content.length > 0
            : Boolean(content && String(content).trim());
          return hasTitle || hasContentTitle || hasContent;
        };

        const processedArticles = articlesData
          .map((a: any) => {
            if (
              a.content &&
              typeof a.content === "object" &&
              (a.content.en || a.content.ru)
            ) {
              const langContent = a.content[currentLang];
              if (!hasLangContent(langContent)) return null; // hide when no content in selected lang

              return {
                ...a,
                title: langContent.title || "",
                meta: langContent.meta || "",
                contentTitle: langContent.contentTitle || "",
                content: langContent.content || "",
              };
            }

            // Hardcoded article
            return {
              ...a,
              title: t(a.titleKey),
              meta: t(a.metaKey),
              contentTitle: t(a.contentTitleKey),
              content: t(a.contentKey, { returnObjects: true }),
            };
          })
          .filter(Boolean);
        setArticles(processedArticles);
      } catch (error) {
        console.error("Error loading articles:", error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, [t, i18n.language]);

  const article = articles.find((a: any) => a.slug === slug);

  // Navigation logic
  const currentIndex = articles.findIndex((a: any) => a.slug === slug);
  const previousArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle =
    currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            component="span"
            className="MuiCircularProgress-root"
            sx={{ width: 40, height: 40 }}
          />
        </Box>
      </Container>
    );
  }

  if (!article) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h5">Статья не найдена</Typography>
        <Button
          component={RouterLink}
          to="/research-and-articles"
          startIcon={<ArrowBackIosIcon />}
          sx={{ mt: 2 }}
        >
          Назад к статьям
        </Button>
      </Container>
    );
  }

  const domain = "https://lolitapapacosta.netlify.app"; //"https://lolitapapacosta.com";
  const shareUrl = `${domain}/research-and-articles/article/${article.slug}`;

  // Defensive: always treat content as an array of blocks
  let contentBlocks: {
    type: string;
    text?: string;
    src?: string;
    alt?: string;
    items?: string[];
  }[] = [];
  if (Array.isArray(article.content)) {
    contentBlocks = article.content.filter(
      (block: any) =>
        typeof block === "object" && block !== null && "type" in block
    );
  } else if (typeof article.content === "string") {
    contentBlocks = [{ type: "paragraph", text: article.content }];
  }

  // Check if the first block is an image, to avoid double image at the top
  const hasTopImageBlock =
    contentBlocks.length > 0 && contentBlocks[0].type === "image";

  let author = "";
  let date = "";
  if (article.meta) {
    const metaParts = article.meta.split("|");
    if (metaParts.length > 1) {
      // Remove 'By ' from author
      author = metaParts[0].trim();
      date = metaParts[1].trim();
    }
  }

  // --- Author Info from translations ---
  const authorDescription = t("author");
  const authorName = "Lolita Papacosta";

  // --- Icons ---
  const iconMap: any = {
    facebook: <FacebookIcon />,
    twitter: <TwitterIcon />,
    whatsapp: <WhatsAppIcon />,
    pinterest: <PinterestIcon />,
    email: <EmailIcon />,
  };

  // --- Share URLs ---
  const shareText = encodeURIComponent(article.contentTitle);
  const shareLinks = [
    {
      icon: "facebook",
      url: `https://www.facebook.com/sharer.php?u=${encodeURIComponent(
        shareUrl
      )}&t=${shareText}`,
      label: "Facebook",
    },
    {
      icon: "twitter",
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        shareUrl
      )}&text=${shareText}`,
      label: "Twitter",
    },
    {
      icon: "whatsapp",
      url: `https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`,
      label: "WhatsApp",
    },
    {
      icon: "pinterest",
      url: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
        shareUrl
      )}&description=${shareText}`,
      label: "Pinterest",
    },
    {
      icon: "email",
      url: `mailto:?subject=${shareText}&body=${encodeURIComponent(shareUrl)}`,
      label: "Email",
    },
  ];

  return (
    <>
      <Helmet>
        <title>{article.contentTitle}</title>
        <meta name="description" content={article.meta} />
        <meta property="og:title" content={article.contentTitle} />
        <meta
          property="og:description"
          content={(() => {
            let description = "";
            if (Array.isArray(article.content)) {
              const firstParagraph = article.content.find(
                (block: blockType) => block.type === "paragraph"
              );
              description = firstParagraph?.text || "";
            } else if (typeof article.content === "string") {
              description = article.content;
            }

            // Clean the description: remove extra whitespace and newlines
            if (description) {
              description = description
                .replace(/\n/g, " ") // Replace newlines with spaces
                .replace(/\s+/g, " ") // Replace multiple spaces with single space
                .trim(); // Remove leading/trailing whitespace

              // Truncate to ~200 characters
              if (description.length > 200) {
                description = description.substring(0, 200) + "...";
              }
            } else {
              // Fallback to meta description
              description = article.meta || "";
            }

            return description;
          })()}
        />
        <meta
          property="og:image"
          content={`${domain}${article.imageDetails}`}
        />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="article" />
        <meta
          property="og:site_name"
          content="Lolita Papacosta - Clinical Psychologist"
        />
        <meta property="og:locale" content="en_US" />
      </Helmet>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Navigation buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Back button */}
          <Button
            component={RouterLink}
            to="/research-and-articles"
            startIcon={<ArrowBackIosIcon />}
          >
            {t("articleDetail.back")}
          </Button>

          {/* Previous/Next navigation */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {previousArticle && (
              <Button
                component={RouterLink}
                to={`/research-and-articles/article/${previousArticle.slug}`}
                startIcon={<ArrowBackIosIcon />}
                size="small"
              >
                {t("articleDetail.previous")}
              </Button>
            )}
            {nextArticle && (
              <Button
                component={RouterLink}
                to={`/research-and-articles/article/${nextArticle.slug}`}
                endIcon={<ArrowForwardIosIcon />}
                size="small"
              >
                {t("articleDetail.next")}
              </Button>
            )}
          </Box>
        </Box>
        <Box sx={{ width: "100%", mb: 4 }}>
          {/* Only render main image if not already present as a content block at the top */}
          {!hasTopImageBlock && (
            <Box
              component="img"
              src={article.imageDetails}
              alt={article.title}
              sx={{
                width: "100%",
                maxHeight: 400,
                objectFit: "cover",
                borderRadius: 2,
                mb: 3,
              }}
            />
          )}
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {article.contentTitle}
          </Typography>
          {/* Article content blocks */}
          {contentBlocks.map((block, idx) => {
            if (block.type === "subtitle") {
              return (
                <Typography
                  key={idx}
                  variant="h6"
                  sx={{ fontWeight: 700, mt: 3, mb: 1 }}
                >
                  {block.text}
                </Typography>
              );
            }
            if (block.type === "paragraph") {
              return (
                <Typography
                  key={idx}
                  variant="body1"
                  sx={{ mb: 2, fontSize: 18, lineHeight: 1.7 }}
                  dangerouslySetInnerHTML={{ __html: block.text || "" }}
                />
              );
            }
            if (block.type === "bullets") {
              return (
                <Box key={idx} component="ul" sx={{ pl: 4, mb: 2 }}>
                  {Array.isArray(block.items) &&
                    block.items.map((item, i) => (
                      <li
                        key={i}
                        style={{
                          fontSize: 18,
                          lineHeight: 1.7,
                          marginBottom: 8,
                        }}
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    ))}
                </Box>
              );
            }
            if (block.type === "numbered") {
              return (
                <Box key={idx} component="ol" sx={{ pl: 4, mb: 2 }}>
                  {Array.isArray(block.items) &&
                    block.items.map((item, i) => (
                      <li
                        key={i}
                        style={{
                          fontSize: 18,
                          lineHeight: 1.7,
                          marginBottom: 8,
                        }}
                      >
                        {item}
                      </li>
                    ))}
                </Box>
              );
            }
            if (block.type === "link") {
              return (
                <Typography
                  key={idx}
                  variant="body1"
                  sx={{ mb: 2, fontSize: 18, lineHeight: 1.7 }}
                >
                  <Link
                    href={(block as any).href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {(block as blockType).text}
                  </Link>
                </Typography>
              );
            }
            if (block.type === "image") {
              return (
                <Box
                  key={idx}
                  component="img"
                  src={block.src}
                  alt={block.alt || "article image"}
                  sx={{
                    width: "100%",
                    maxHeight: 400,
                    objectFit: "contain",
                    borderRadius: 2,
                    mb: 3,
                    mt: 3,
                    display: "block",
                    mx: "auto",
                  }}
                />
              );
            }
            return null;
          })}
        </Box>
        {/* Meta and Share Section BELOW content, above author */}
        <Box sx={{ width: "100%", mt: 4, mb: 4 }}>
          <Box
            component="hr"
            sx={{ border: 0, borderTop: "1px solid #e0e0e0", mb: 2 }}
          />
          <Box
            sx={{
              color: "text.secondary",
              fontSize: 15,
              fontWeight: 700,
              mb: 2,
            }}
          >
            {author && <span>{author}</span>}
            {date && <span> | {date}</span>}
          </Box>
          <Box
            component="hr"
            sx={{ border: 0, borderTop: "1px solid #e0e0e0", mb: 2 }}
          />
          <Box
            sx={{
              background: "#f5f5f5",
              borderRadius: 2,
              px: 2,
              py: 2,
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t("articleDetail.share")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {shareLinks.map((link) => (
                <Button
                  key={link.label}
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ minWidth: 0, p: 1, borderRadius: 1, color: "#555" }}
                  aria-label={link.label}
                >
                  {iconMap[link.icon]}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
        {/* About the Author Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            mt: 6,
            background: "#fafafa",
            borderRadius: 2,
            p: 3,
          }}
        >
          <Box
            component="img"
            src="/images/author.jpeg"
            alt={authorName}
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              objectFit: "cover",
              mr: 2,
            }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t("articleDetail.aboutAuthor")}{" "}
              {/* <span style={{ fontWeight: 400 }}>{authorName}</span> */}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", fontSize: 18, lineHeight: 1.7 }}
            >
              {authorDescription}
            </Typography>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default ArticleDetail;
